import classNames from 'classnames'
import { t } from 'i18next'
import { ChangeEvent, SetStateAction, useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { sendFulfillment } from '../../../api/policyapi'
import childPlanFormDataContext from '../../../contexts/ChildPlanFormDataContext'
import ParentFormDataContext from '../../../contexts/ParentFormDataContext'
import PaymentFormDataContext from '../../../contexts/PaymentFormDataContext'
import { ResponsiveWidth } from '../../../contexts/contextDataObjects/ParentDataObject'
import { IPolicyFulfillment } from '../../../model/IPolicyFulfillment'
import { IPolicyResponse } from '../../../model/IPolicyReponse'
import { formatCAD } from '../../../utils/currencyFormatHelper'
import { getDatePickerPattern, getEmailPattern, getSpecialCharacterPattern } from '../../../utils/patternHelper'
import Button from '../../button/Button'
import InputBox from '../../inputBox/InputBox'
import ChildSelectedPremiumsTable from '../confirmation/ChildSelectedPremiumsTable'

export interface IPolicyReceipt {
    className?: string
    policyResponse: IPolicyResponse
    setShowConfirmation: React.Dispatch<SetStateAction<boolean>>
}

const PolicyReceipt: React.FC<IPolicyReceipt> = ({
    className,
    policyResponse,
    setShowConfirmation
}) => {
    const { parentFormData, setParentFormData } = useContext(
        ParentFormDataContext
    )

    const { childPlanFormData, setChildPlanFormData } = useContext(
        childPlanFormDataContext
    )

    const { paymentFormData, setPaymentFormData } = useContext(
        PaymentFormDataContext
    )

    const [anotherEmail, setAnotherEmail] = useState("")
    const [anotherEmailIsBlur, setAnotherEmailIsBlur] = useState(false)
    const [anotherEmailIsFocus, setAnotherEmailIsFocus] = useState(false)
    const [anotherEmailIsValid, setAnotherEmailIsValid] = useState(false)
    const [anotherEmailError, setAnotherEmailError] = useState("")

    const navigate = useNavigate()

    const options: Intl.DateTimeFormatOptions = {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
    };

    useEffect(() => {
        window.scrollTo(0, 0)
    }, [])

    const childPremiums = childPlanFormData.map((child, index) => (
        <ChildSelectedPremiumsTable key={index} childPlan={child} className="pt-4" />
    ))

    const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
        setAnotherEmail(e.target.value.trim())
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { value } = e.target

        // Validation - pattern match
        const regex = RegExp(getEmailPattern())

        if (value && !regex.test(value)) {
            setAnotherEmailIsValid(false)
            setAnotherEmailError("emailpatternerror")
        } else {

            setAnotherEmailIsValid(true)
        }

        setAnotherEmailIsBlur(true)
        setAnotherEmailIsFocus(false)
    }

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setAnotherEmailIsBlur(false)
        setAnotherEmailIsFocus(true)
    }

    const handleSubmit = async () => {
        const policyFulfilment: IPolicyFulfillment = {
            PolicyNum: policyResponse.policyNum,
            Email: anotherEmail,
            CCEmail: '',
            Language: Number(policyResponse.language),
        };

        const res = await sendFulfillment(policyFulfilment)

        if (res) {
            toast.success(`${t('fulfilmentemailsuccess')} ${anotherEmail}`);
        } else {
            toast.error('Error')
        }

        setAnotherEmail('')
    }

    const returnHome = () => {
        let returnURL = parentFormData.ReturnHome;
        if (parentFormData.Language === 1) {
            if (parentFormData.ReturnHome.includes("insuremykids") || parentFormData.ReturnHome.includes("manitobastudentinsurance")) {
                returnURL = parentFormData.ReturnHome + '/fr'
            }
            else {
                returnURL = parentFormData.ReturnHome + "?lang=1"
            }
        }
        return returnURL
    }

    const resetParentChildPlanInputs = () => {
        setParentFormData({
            ...parentFormData,
            Province: parentFormData.Province,
            FirstName: '',
            LastName: '',
            Address: '',
            City: '',
            PostalCode: '',
            PhoneNumber: '',
            Email: '',
            DoNotOffer: false,
            OrgGuid: '',
            SchoolBoard: {
                id: '',
                name: '',
            },
            PlanDuration: {
                DurationText: '',
                Duration: 1,
                Discount: '',
                Index: 0,
            },
            AutoEnroll: {
                Renew: '',
                Index: -1,
                IsValid: false,
            },
        })

        const resetChildPlanFormData = [{
            FirstName: '',
            LastName: '',
            Dob: '',
            School: { id: '', name: '' },
            Plan: {
                PlanCode: '',
                Primary: '',
                Premium: 0,
                Index: -1,
                IsValid: false
            },
            ChildPlanFormDataInput: [{
                Id: 'ChildFnInput',
                Name: 'FirstName',
                Type: 'text',
                ControlType: 'InputBox',
                Placeholder: 'First Name.',
                Label: 'firstName',
                Required: true,
                ClassName: 'pt-2 lg:ml-2',
                Width: classNames(ResponsiveWidth, "border-[1.5px] border-gray-400"),
                Height: 'h-10',
                Shadow: 'shadow-md',
                ErrorMessageList: [
                    { type: 'required', error: 'required' },
                    { type: 'pattern', error: 'specialcharpatternerror' },
                ],
                ErrorMessage: '',
                Pattern: getSpecialCharacterPattern(),
                IsValid: false,
                IsBlur: false,
                IsFocus: false,
                Disabled: false,
            },
            {
                Id: 'ChildLnInput',
                Name: 'LastName',
                Type: 'text',
                ControlType: 'InputBox',
                Placeholder: 'Last Name.',
                Label: 'lastName',
                Required: true,
                ClassName: 'pt-2',
                Width: classNames(ResponsiveWidth, "border-[1.5px] border-gray-400"),
                Height: 'h-10',
                Shadow: 'shadow-md',
                ErrorMessageList: [
                    { type: 'required', error: 'required' },
                    { type: 'pattern', error: 'specialcharpatternerror' },
                ],
                ErrorMessage: '',
                Pattern: getSpecialCharacterPattern(),
                IsValid: false,
                IsBlur: false,
                IsFocus: false,
                Disabled: false,
            },
            {
                Id: 'ChildDobPicker',
                Name: 'Dob',
                Type: '',
                ControlType: 'DatePicker',
                Placeholder: 'selectDOB',
                Label: 'dob',
                Required: true,
                ClassName: classNames('mt-2 lg:mr-1.5 mr-0 '),
                Width: '',
                Height: 'h-10',
                Shadow: '',
                ErrorMessageList: [{ type: 'required', error: 'required' }],
                ErrorMessage: '',
                Pattern: getDatePickerPattern(),
                IsValid: false,
                IsBlur: false,
                IsFocus: false,
                Disabled: false,
            },
            {
                Id: 'SchoolDDL',
                Name: 'School',
                Type: '',
                ControlType: 'DropDownList',
                ListItems: [{ id: '', name: '' }],
                Placeholder: '',
                Label: 'school',
                Required: true,
                ClassName: 'z-0 lg:mb-11 lg:pr-1 pr-0 lg:pt-1 ',
                Width: classNames(ResponsiveWidth),
                Height: 'h-[2.4rem]',
                Shadow: '',
                ErrorMessageList: [{ type: 'required', error: 'required' }],
                ErrorMessage: '',
                Pattern: '',
                IsValid: false,
                IsBlur: false,
                IsFocus: false,
                Disabled: false,
            }],
            SetValidationFocus: false
        }]

        setChildPlanFormData(resetChildPlanFormData)

        return true
    }

    return (
        <div className='bg-[#f5f5f5] lg:h-[78.5vh] h-[82vh]'>
            <div className="bg-[#0082ca] h-auto items-center md:py-2 mt-2 py-3 w-full flex flex-col">
                <p className="text-black text-center font-light lg:text-base text-sm lg:w-[55rem] md:w-[31rem] w-[19rem] pt-1.5">
                    {t('thankPurchase')}
                    <br></br>
                    {t('emailConfirmation')}
                </p>
            </div>
            <div className='flex justify-center pt-7'>
                <div className='h-auto flex flex-col items-center bg-white lg:w-[55rem] md:w-[31rem] w-[20rem] shadow-md py-5'>
                    <div className='grid grid-cols-2 pt-2 justify-center md:text-base text-sm lg:ml-[8.5rem] md:ml-[6rem] lg:w-[35rem] md:w-[30rem] w-[18rem]'>
                        <div className='pb-4'>
                            <h1 className='font-bold'>
                                {t('policyNumber')}
                            </h1>
                            <p className=''>
                                {policyResponse.policyNum}
                            </p>
                        </div>
                        <div className='md:ml-0 ml-2 md:w-32 md:pb-2'>
                            <h1 className='font-bold'>
                                {t('startDate')}
                            </h1>
                            <p className=''>
                                {new Date(policyResponse.policy.departuredate).toLocaleString('en-US', options)}
                            </p>
                        </div>
                        <div className='pb-4'>
                            <p className='font-bold'>
                                {paymentFormData.CCType.name} {t('endingIn')}
                            </p>
                            <p className=''>
                                {policyResponse.cardNo?.substring(policyResponse.cardNo.length - 4)}
                            </p>
                        </div>
                        <div className='md:ml-0 ml-2'>
                            <h1 className='font-bold'>
                                {t('phoneNum')}
                            </h1>
                            <p className=''>
                                {policyResponse.contactInfo.dayphone}
                            </p>
                        </div>
                        <div className='pb-4'>
                            <h1 className='font-bold'>
                                {t('duration')}
                            </h1>
                            <p className=''>
                                {parentFormData.PlanDuration.Duration}{parentFormData.PlanDuration.Duration == 1 ? t('year') : t('years')}
                            </p>
                        </div>
                        <div className='md:ml-0 ml-2'>
                            <h1 className='font-bold'>
                                {t('totalCost')}
                            </h1>
                            <p className=''>
                                {formatCAD(parentFormData.TotalPremium, parentFormData.Language)}
                            </p>
                        </div>
                        <div className='col-span-2 pb-2'>
                            <h1 className='font-bold '>
                                {t('emailAddress')}
                            </h1>
                            <p className=''>
                                {policyResponse.contactInfo.emailaddress}
                            </p>
                        </div>
                    </div>
                    <div className='mt-4 flex justify-center border-[1.5px] border-gray-400'>
                        <table className='text-left bg-white shadow-md  lg:w-[26.7rem] md:w-[24rem] w-[18.5rem]'>
                            <thead>
                                <tr className='border-2 border-white py-2'>
                                    <th className='text-black p-1 w-[8rem]'>{t('name')}</th>
                                    <th className='text-black p-1 w-[12rem]'>{t('plan')}</th>
                                    <th className='text-black p-1 text-right'>{t('premium')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {childPremiums}
                            </tbody>
                        </table>
                    </div>
                    <div className='flex flex-row justify-center space-x-2.5'>
                        <div className='flex flex-col justify-center'>
                            <label htmlFor="altEmail" className='pt-6 lg:text-base md:text-sm text-xs lg:w-[14rem] md:w-[12rem] w-[10rem]'>{t('sendemailto')}</label>
                            <div><InputBox
                                type="text"
                                inputID="altEmail"
                                placeholder=''
                                width="lg:w-[16rem] md:w-[15rem] w-[12.3rem] border-[1.5px] border-gray-400 "
                                minWidth='min-w-[10rem]'
                                height="h-10"
                                shadow="shadow-md"
                                onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                ) => handleChange(e)}
                                onBlur={(
                                    e: React.FocusEvent<HTMLInputElement>
                                ) => handleBlur(e)}
                                onFocus={(
                                    e: React.FocusEvent<HTMLInputElement>
                                ) => handleFocus(e)}
                                value={anotherEmail}
                                name={'altEmail'}
                                error={t(anotherEmailError) || ''}
                                isValid={anotherEmailIsValid}
                                isBlur={anotherEmailIsBlur}
                                isFocus={anotherEmailIsFocus}
                            />
                            </div>
                        </div>
                        <div className='flex items-center lg:pt-9 md:pt-8 pt-6'>
                            <Button className='bg-[#9ccb45] lg:w-[9.9rem] md:w-[8rem] w-[5.4rem] h-10 hover:bg-opacity-60 font-bold lg:mt-7 mt-6 cursor-pointer shadow-md border-[1.5px] border-gray-400'
                                textColor='black'
                                textSize='lg:text-base md:text-sm text-xs'
                                text={t('Send')}
                                disabled={!anotherEmail || !anotherEmailIsValid}
                                handleClick={() => { handleSubmit() }} />
                        </div>
                    </div>
                    <div className='flex flex-row justify-center space-x-5 pb-2'>
                        <a rel='noopener noreferrer' href={returnHome()} >
                            <Button className='bg-white hover:bg-gray-200 h-12 mt-5 shadow-md font-bold md:px-[4.8rem] px-[2rem] hover:bg-opacity-50 border-[1.5px] border-gray-400 lg:w-[26.7rem] md:w-[24rem] w-[18.5rem]'
                                textColor='black'
                                textSize='lg:text-base md:text-sm text-xs'
                                text={t('Home')}
                                handleClick={() => { }} />
                        </a>
                    </div>

                </div>
            </div>
        </div>
    )
}

export default PolicyReceipt
